import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // Necessary for handling UTC times correctly
import { toast } from "react-toastify";
import utils from "../../../../utils/utils";

dayjs.extend(utc); // Extend dayjs with UTC plugin

function convertTo24Hour(timeStr) {
  // Check if the input already contains "AM" or "PM"; if not, assume it's already in 24-hour format
  if (!timeStr.includes("AM") && !timeStr.includes("PM")) {
    return timeStr; // Return the original string if it's already in 24-hour format
  }

  const [time, modifier] = timeStr.split(" ");
  let [hours, minutes] = time.split(":");

  // Convert "12 AM" to "00:00" (midnight) and handle "12 PM" correctly
  if (hours === "12") {
    hours = modifier === "AM" ? "00" : "12";
  } else if (modifier === "PM") {
    hours = parseInt(hours, 10) + 12; // Convert PM hours to 24-hour format
  }

  return `${hours}:${minutes}`;
}

function isTimeInThePast(dateStr, timeStr) {
  // Parse the provided date and time, and convert to a Day.js object
  const dateTime = dayjs(
    `${dateStr} ${convertTo24Hour(timeStr)}`,
    "YYYY-MM-DD HH:mm"
  )
    .utc()
    .local()
    .startOf("minute");

  // Get the current date and time, converted to a Day.js object, and set seconds to zero
  const currentTime = dayjs().utc().local().startOf("minute");

  // Compare the two times, checking if dateTime is strictly before currentTime
  return dateTime.isBefore(currentTime);
}

export const validateForm = (formData, recipientsData, expire, schedule) => {
  const errors = {};
  const today = dayjs().startOf("day").utc().local();

  if (!formData.notificationType) {
    errors.notificationType = "Notification type is required";
  }
  if (!formData.title.trim()) {
    errors.title = "Title is required";
  }
  if (utils.isEmpty(formData.messageContent)) {
    errors.messageContent = "Message content is required";
  }
  if (utils.isEmpty(recipientsData)) {
    errors.recipients = "At least one recipient is required";
  }

  if (expire) {
    if (!formData.expiryDate) {
      errors.expiryDate = "Expiry date is required";
    } else if (
      dayjs(formData.expiryDate, "DD-MM-YYYY").isBefore(today, "day")
    ) {
      errors.expiryDate = "Expiry date must be in the future";
    }
    if (!formData.expiryTime) {
      errors.expiryTime = "Expiry time is required";
    }
  }

  if (schedule) {
    if (!formData.scheduleDate) {
      errors.scheduleDate = "Schedule date is required";
    } else {
      const scheduleDate = dayjs(formData.scheduleDate, "DD-MM-YYYY").format(
        "YYYY-MM-DD"
      );
      if (dayjs(scheduleDate).isBefore(today, "day")) {
        errors.scheduleDate = "Schedule date must be in the future";
      } else if (
        formData.scheduleTime &&
        isTimeInThePast(scheduleDate, formData.scheduleTime)
      ) {
        errors.scheduleTime = "Schedule time must be in the future";
      }
    }
    if (!formData.scheduleTime) {
      errors.scheduleTime = "Schedule time is required";
    }
  }

  if (formData.notificationType !== "General") {
    if (
      formData.startDate ||
      formData.startTime ||
      formData.endDate ||
      formData.endTime
    ) {
      if (!formData.startDate) {
        errors.startDate = "Start date is required";
      } else {
        const startDate = dayjs(formData.startDate, "DD-MM-YYYY").format(
          "YYYY-MM-DD"
        );
        if (dayjs(startDate).isBefore(today, "day")) {
          errors.startDate = "Start date must be in the future";
        } else if (
          formData.startTime &&
          isTimeInThePast(startDate, formData.startTime)
        ) {
          errors.startTime = "Start time must be in the future";
        }
      }

      if (!formData.startTime) {
        errors.startTime = "Start time is required";
      }

      if (!formData.endDate) {
        errors.endDate = "End date is required";
      } else {
        const endDate = dayjs(formData.endDate, "DD-MM-YYYY").format(
          "YYYY-MM-DD"
        );
        if (dayjs(endDate).isBefore(dayjs(formData.startDate, "DD-MM-YYYY"))) {
          errors.endDate = "End date must be after the start date";
        } else if (
          formData.endTime &&
          isTimeInThePast(endDate, formData.endTime)
        ) {
          errors.endTime = "End time must be in the future";
        }
      }

      if (!formData.endTime) {
        errors.endTime = "End time is required";
      }
    }
  }

  return errors;
};

export const draftValidation = (
  formData,
  recipientsData,
  schedule,
  expire,
  handleSave
) => {
  let errors = [];
  const today = dayjs().startOf("day").utc().local();

  if (!formData.title.trim()) {
    errors.push("Title is required to save draft");
  }
  if (utils.isEmpty(recipientsData)) {
    errors.push("At least one recipient is required");
  }

  if (schedule && formData.scheduleDate) {
    const scheduleDate = dayjs(formData.scheduleDate, "DD-MM-YYYY").format(
      "YYYY-MM-DD"
    );
    if (dayjs(scheduleDate).isBefore(today)) {
      errors.push("Schedule date must be in the future");
    } else if (
      formData.scheduleTime &&
      isTimeInThePast(scheduleDate, formData.scheduleTime)
    ) {
      errors.push("Schedule time must be in the future");
    }
  }

  if (expire && formData.expiryDate) {
    const expiryDate = dayjs(formData.expiryDate, "DD-MM-YYYY").format(
      "YYYY-MM-DD"
    );
    if (dayjs(expiryDate).isBefore(today)) {
      errors.push("Expiry date must be in the future");
    }
    if (!formData.expiryTime) {
      errors.push("Expiry time is required");
    }
  }

  // Additional checks for General notifications involving start and end dates
  if (formData.notificationType !== "General") {
    // Validate start date and time
    if (formData.startDate) {
      const startDate = dayjs(formData.startDate, "DD-MM-YYYY").format(
        "YYYY-MM-DD"
      );
      if (dayjs(startDate).isBefore(today)) {
        errors.push("Start date must be in the future");
      } else if (
        formData.startTime &&
        isTimeInThePast(startDate, formData.startTime)
      ) {
        errors.push("Start time must be in the future");
      }
    } else if (formData.startTime) {
      errors.push("Start date is required when start time is provided");
    }

    // Validate end date and time
    if (formData.endDate) {
      const endDate = dayjs(formData.endDate, "DD-MM-YYYY").format(
        "YYYY-MM-DD"
      );
      if (!formData.startDate) {
        errors.push("Start date must be specified before the end date");
      } else if (
        dayjs(endDate).isBefore(dayjs(formData.startDate, "DD-MM-YYYY"))
      ) {
        errors.push("End date must be after the start date");
      } else if (
        formData.endTime &&
        isTimeInThePast(endDate, formData.endTime)
      ) {
        errors.push("End time must be in the future");
      }
    } else if (formData.endTime) {
      errors.push("End date is required when end time is provided");
    }
  }

  if (!utils.isEmpty(errors)) {
    errors.forEach((error) => toast.warning(error));
    return;
  }

  handleSave("draft");
};
